import { cn } from '@/lib/helpers/utils';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { AUTHENTICATION, broadcastHandler } from '../../app/broadcastChannels/channels';
import { useBookmarkHandle } from '../../app/hooks/useBookmarkHandle';
import { useLinkLogin } from '../../app/hooks/useLinkLogin';
import linkLoginRedirectAction from '../../app/redux/actions/linkLoginRedirectAction';
import { AppDispatch } from '../../app/redux/configureStore';
import { useAppSelector } from '../../app/redux/hooks';
import { handleAuthStateUpdate } from '../../app/services/auth';
import { generateCssVariables, themes } from '../../assets/styles/themes';
import ModalWrapper from '../globalModal/ModalWrapper';
import NotificationHandler from '../notifications/NotificationHandler';
import withLocation from '../withLocation';
import Footer from './Footer';
import Header, { HeaderProps } from './Header';

export type Meta = {
  tag: string;
  name: string;
  property?: string;
  content: string;
  href?: string;
  rel?: string;
};

export interface LayoutProps {
  children?: any;
  meta: Meta[];
  minimal?: boolean;
  bodyAttributes?: any;
  structuredData?: any;
  isSignedIn: any;
  linkLoginRedirect: AppDispatch;
  location: any;
  userId: string | null;
  headerProps: HeaderProps;
}

/**
 * The layout
 *
 * @param {Props} param0
 * @return {JSX}
 */
const Layout = ({
  children,
  meta,
  minimal = false,
  bodyAttributes = {},
  structuredData,
  isSignedIn,
  linkLoginRedirect,
  location,
  userId,
  headerProps
}: LayoutProps) => {
  useLinkLogin(isSignedIn, linkLoginRedirect);
  const bookmarkInfo = useAppSelector((state) => state.bookmarkInfo);
  useBookmarkHandle(isSignedIn, bookmarkInfo);

  useEffect(() => {
    broadcastHandler(AUTHENTICATION, (ev: any) => {
      handleAuthStateUpdate(ev.data);
    });
  }, []);

  const metaTags = meta.map((m: Meta, i: number) => {
    if (m.name == 'title') {
      return <title key={'meta-' + i}>{m.content}</title>;
    }
    if (m.tag == 'meta') {
      if (m.name) {
        return <meta key={'meta-' + i} name={m.name} content={m.content} />;
      }
      if (m.property) {
        return <meta key={'meta-' + i} property={m.property} content={m.content} />;
      }
    }
    if (m.tag == 'link') {
      return <link key={'meta-' + i} rel={m.rel} href={m.href} />;
    }
  });

  // Process structured data received into a script tag
  if (structuredData) {
    structuredData = structuredData.filter((data: any) => data && data);
    structuredData.length > 1
      ? (structuredData = <script type="application/ld+json">{`[${structuredData.toString()}]`}</script>)
      : (structuredData = <script type="application/ld+json">{structuredData.toString()}</script>);
  }

  return (
    <>
      <Helmet htmlAttributes={{ lang: 'en' }} bodyAttributes={bodyAttributes}>
        {metaTags}
        {structuredData && structuredData}
        <style type="text/css">
          {`
            ${themes
              .filter((theme: any) => theme.siteBrandName === `${process.env.GATSBY_SITE_NAME}`)
              .map((theme: any) => {
                if (theme.id === 'default') {
                  return `
                  :root {
                    ${generateCssVariables(theme)}
                  }
                `;
                } else if (theme.id === 'dark') {
                  return `
                  @media (prefers-color-scheme: dark) {
                    ${generateCssVariables(theme)}
                  }
                `;
                }
              })
              .join('')}
            ${themes
              .filter((theme: any) => theme.siteBrandName === `${process.env.GATSBY_SITE_NAME}`)
              .map((theme: any) => {
                return `
                [data-theme="${theme.id}"] {
                  ${generateCssVariables(theme)}
                }
              `;
              })
              .join('')}
          `}
        </style>
      </Helmet>
      {!minimal && <Header {...headerProps} />}
      {
        <div id="main-content" className={cn('isolate', !minimal && 'min-h-[calc(100svh_-_60px)] min-w-0 md:min-h-0')}>
          {children}
        </div>
      }
      {!minimal && <Footer />}
      <ModalWrapper location={location} />
      <NotificationHandler userId={userId} isSignedIn={isSignedIn} />
    </>
  );
};

const mapActionsToProps = {
  linkLoginRedirect: linkLoginRedirectAction
};

const mapStateToProps = ({ auth }: any) => {
  return {
    isSignedIn: auth.isSignedIn,
    userId: auth.user?.id
  };
};

export default connect(mapStateToProps, mapActionsToProps)(withLocation(Layout));
